import { useCallback, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGridV2, TONES } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2Column } from '@bloomays-lib/ui.shared/datagrid';
import { AnnualPrimeRowStatus, AnnualPrimeTableRow, AnnualTamPrimeAggregate } from '../../types/primes';
import { buildMonthlyPrimeNavigationTarget } from '../../utils/annualPrime.utils';
import { formatMonthYearToMonthLabel, formatPrimeEuro, formatPrimeEuroWithSign } from '../../utils/monthlyPrime.utils';

export type AnnualTamPrimesTableProps = {
  rows: AnnualPrimeTableRow[];
  loading: boolean;
  annualAggregate?: AnnualTamPrimeAggregate;
  tamEmail: string;
  isTamPreview: boolean;
};

const leftAlignedColumn = {
  align: 'left' as const,
  headerAlign: 'left' as const,
};

const getPrimeStatusDotColor = (status: AnnualPrimeRowStatus, theme: Theme) =>
  status === 'validated' ? theme.palette.green[100] : theme.palette.beige[100];

const AnnualTamPrimesTable = ({
  rows,
  loading,
  annualAggregate,
  tamEmail,
  isTamPreview,
}: AnnualTamPrimesTableProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['primes']);
  const navigate = useNavigate();

  const handleNavigateToMonth = useCallback(
    (row: AnnualPrimeTableRow) => {
      if (loading || !annualAggregate || !tamEmail) {
        return;
      }
      const target = buildMonthlyPrimeNavigationTarget({
        aggregate: annualAggregate,
        row,
        tamEmail,
        isTamPreview,
      });
      navigate({ pathname: target.pathname, search: target.search }, { state: target.state });
    },
    [annualAggregate, isTamPreview, loading, navigate, tamEmail],
  );

  const columns: DataGridV2Column<AnnualPrimeTableRow>[] = useMemo(
    () => [
      {
        field: 'monthYear',
        header: t('columnMonth'),
        minWidth: 120,
        flex: 0.8,
        variant: 'custom',
        render: (row) => (
          <MonthYearCell>
            <MonthYearText>{formatMonthYearToMonthLabel(row.monthYear)}</MonthYearText>
            <StatusDot
              $color={getPrimeStatusDotColor(row.status, theme)}
              title={row.status === 'validated' ? t('monthStatusValidated') : t('monthStatusPending')}
            />
          </MonthYearCell>
        ),
        ...leftAlignedColumn,
      },
      {
        field: 'actions',
        header: t('columnAction'),
        width: 72,
        sortable: false,
        filterable: false,
        variant: 'custom',
        render: (row) => (
          <IconButton
            size="small"
            disabled={loading || !annualAggregate}
            aria-label={t('annualActionMonthlyDetail')}
            onClick={() => handleNavigateToMonth(row)}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        ),
        ...leftAlignedColumn,
      },
      {
        field: 'totalFreelanceMarge',
        header: t('columnFreelanceMarge'),
        minWidth: 130,
        flex: 1,
        variant: 'custom',
        valueGetter: (row) => row.totalFreelanceMarge,
        render: (row) => <MoneyText>{formatPrimeEuro(row.totalFreelanceMarge)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'totalBillingMarge',
        header: t('columnBillingMarge'),
        minWidth: 120,
        flex: 1,
        variant: 'custom',
        valueGetter: (row) => row.totalBillingMarge,
        render: (row) => <MoneyText>{formatPrimeEuro(row.totalBillingMarge)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'totalMarge',
        header: t('columnTotalMarge'),
        minWidth: 120,
        flex: 1,
        variant: 'custom',
        valueGetter: (row) => row.totalMarge,
        render: (row) => <MoneyText>{formatPrimeEuro(row.totalMarge)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'totalPrime',
        header: t('columnCommission'),
        minWidth: 120,
        flex: 1,
        variant: 'custom',
        valueGetter: (row) => row.totalPrime,
        render: (row) => <MoneyText>{formatPrimeEuro(row.totalPrime)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'bonus',
        header: t('columnBonus'),
        minWidth: 110,
        flex: 0.9,
        variant: 'custom',
        valueGetter: (row) => row.bonus,
        render: (row) => <MoneyText>{formatPrimeEuro(row.bonus)}</MoneyText>,
        ...leftAlignedColumn,
      },
      {
        field: 'regularisation',
        header: t('columnRegularisation'),
        minWidth: 140,
        flex: 1,
        valueGetter: (row) => formatPrimeEuroWithSign(row.regularisation),
        ...leftAlignedColumn,
      },
      {
        field: 'totalDeals',
        header: t('columnDeals'),
        minWidth: 100,
        flex: 0.7,
        ...leftAlignedColumn,
      },
      {
        field: 'totalMissions',
        header: t('columnMissions'),
        minWidth: 110,
        flex: 0.8,
        ...leftAlignedColumn,
      },
    ],
    [annualAggregate, handleNavigateToMonth, loading, t, theme],
  );

  return (
    <DataGridV2<AnnualPrimeTableRow>
      uniqueId="annual-tam-primes-table"
      rows={rows}
      columns={columns}
      loading={loading}
      loadingVariant="bar"
      getRowId={(row) => row.rowId}
      search={false}
      enableExport={false}
      enableColumnSelector={false}
      enableFilterPanel={false}
      pagination={rows.length > 12}
      pageSize={12}
      hideFooter={rows.length <= 12}
    />
  );
};

export default AnnualTamPrimesTable;

/** Reproduit le style de `MoneyCell` (ui.shared) pour un montant à 2 décimales fixes. */
const MoneyText = styled('span')(() => ({
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  color: TONES.success.color,
}));

const MonthYearCell = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const MonthYearText = styled('span')(() => ({
  fontSize: 13,
  whiteSpace: 'nowrap',
}));

const StatusDot = styled('span', {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color: string }>(({ $color }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: $color,
}));
